import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import {
  budgetCodeSchema,
  COLLECTIONS,
  type BudgetCode,
  type NewExternalBudgetCode,
  type NewOwnedBudgetCode,
} from '@budgetmonkey/shared'
import { db } from '@/lib/firebase'
import { converterFor, nowIso, toIso } from '@/lib/firestore'

const TIMESTAMP_FIELDS = ['createdAt', 'updatedAt'] as const

const budgetCodeConverter = converterFor(budgetCodeSchema, TIMESTAMP_FIELDS)

const budgetCodesRef = () =>
  collection(db, COLLECTIONS.budgetCodes).withConverter(budgetCodeConverter)

const budgetCodeRef = (id: string) =>
  doc(db, COLLECTIONS.budgetCodes, id).withConverter(budgetCodeConverter)

/**
 * Every budget code for a user and fiscal year — owned and external together,
 * active and inactive.
 *
 * Fetched as one query rather than filtered server-side because the whole set
 * is ~11 documents and every consumer wants a different slice of it: the
 * dashboard wants active owned codes, the picker wants both kinds grouped, and
 * lookup needs the full set to judge which code segments are distinctive
 * (a segment's usefulness depends on what else exists). One cached query beats
 * four filtered ones.
 */
export async function fetchBudgetCodes(
  userId: string,
  fiscalYear: number,
): Promise<BudgetCode[]> {
  const snapshot = await getDocs(
    query(
      budgetCodesRef(),
      where('userId', '==', userId),
      where('fiscalYear', '==', fiscalYear),
    ),
  )
  return snapshot.docs.map((d) => d.data())
}

/**
 * Every budget code the user has, in any fiscal year.
 *
 * For naming a charge's code rather than for choosing one. Codes are per-year
 * documents (§11), so a charge that belongs to last year still points at the
 * document it was filed against — and a lookup scoped to the year being viewed
 * cannot resolve it, which showed a June charge on a July statement with no
 * code beside it at all. An id identifies exactly one document, so resolving
 * it across years is unambiguous; it is only *pickers* that must stay scoped,
 * because a new charge has to be filed against this year's code.
 *
 * Equality on `userId` alone, so no composite index (see CLAUDE.md).
 */
export async function fetchAllBudgetCodes(userId: string): Promise<BudgetCode[]> {
  const snapshot = await getDocs(
    query(collection(db, COLLECTIONS.budgetCodes), where('userId', '==', userId)),
  )

  // Parsed one document at a time, deliberately not through the converter.
  //
  // The converter throws on a document that fails its schema, and a screen
  // turns that throw into an empty result — which is right for a query scoped
  // to one year, where the alternative is showing a wrong set of codes. It is
  // wrong here. This query spans every year purely to put a name on a charge's
  // code, so one malformed document in a year nobody is looking at would blank
  // the code on every charge, everywhere, with nothing on screen to say why.
  //
  // A code that cannot be read is skipped and the rest still resolve. Same
  // trade `fetchReceiptsWithProblems` makes, for the same reason.
  const codes: BudgetCode[] = []
  for (const document of snapshot.docs) {
    const raw: Record<string, unknown> = { ...document.data(), id: document.id }
    for (const field of TIMESTAMP_FIELDS) {
      if (field in raw) raw[field] = toIso(raw[field])
    }
    const parsed = budgetCodeSchema.safeParse(raw)
    if (parsed.success) {
      codes.push(parsed.data)
    } else {
      // Not silent: this is the only place a bad code document is noticed at
      // all, and a name quietly missing from a packet is worth a console line.
      console.warn(
        `Budget code ${document.id} could not be read and was skipped:`,
        parsed.error.issues.map((i) => `${i.path.join('.')} ${i.message}`).join('; '),
      )
    }
  }
  return codes
}

/**
 * Copy a year's codes into another year — the July rollover, and the way to
 * give a past year the codes its charges were filed against.
 *
 * §11 rules out *automatic* carryover, and this is not that: it is asked for,
 * and it is re-runnable because a code already present in the target year is
 * skipped rather than duplicated — matched on the account code, which is what
 * stays the same from year to year while balances do not.
 *
 * Beginning balances come across as they are. They are the one thing that
 * genuinely differs between years, so they are a starting point to correct,
 * not an answer — which is why the screen says so before this runs.
 */
export async function copyBudgetCodesToYear(
  userId: string,
  fromYear: number,
  toYear: number,
): Promise<{ copied: number; skipped: number }> {
  const [source, existing] = await Promise.all([
    fetchBudgetCodes(userId, fromYear),
    fetchBudgetCodes(userId, toYear),
  ])
  const alreadyThere = new Set(existing.map((c) => c.code))
  const toCopy = source.filter((c) => !alreadyThere.has(c.code))
  if (toCopy.length === 0) return { copied: 0, skipped: source.length }

  const batch = writeBatch(db)
  const timestamp = nowIso()
  for (const code of toCopy) {
    batch.set(budgetCodeRef(doc(collection(db, COLLECTIONS.budgetCodes)).id), {
      ...code,
      id: undefined,
      userId,
      fiscalYear: toYear,
      active: true,
      createdAt: timestamp,
      updatedAt: timestamp,
    } as BudgetCode)
  }
  await batch.commit()
  return { copied: toCopy.length, skipped: source.length - toCopy.length }
}

export async function createOwnedBudgetCode(
  userId: string,
  fiscalYear: number,
  input: NewOwnedBudgetCode,
): Promise<string> {
  const timestamp = nowIso()
  const ref = await addDoc(budgetCodesRef(), {
    ...input,
    userId,
    fiscalYear,
    external: false,
    externalOwnerName: null,
    active: true,
    createdAt: timestamp,
    updatedAt: timestamp,
  } as BudgetCode)
  return ref.id
}

/**
 * Returns the created document rather than just its ID: workflow 7.7 step 6
 * applies the new code to the current transaction immediately, and the cached
 * code list hasn't refetched yet at that point.
 */
export async function createExternalBudgetCode(
  userId: string,
  fiscalYear: number,
  input: NewExternalBudgetCode,
): Promise<BudgetCode> {
  const timestamp = nowIso()
  const document = {
    ...input,
    userId,
    fiscalYear,
    // External codes take no part in budget math (§5.2), so these are inert.
    displayName: input.externalOwnerName,
    shortLabel: input.shortLabel?.trim() || input.externalOwnerName,
    fund: 'main',
    category: 'other',
    external: true,
    externalOwnerName: input.externalOwnerName,
    beginningBalance: 0,
    alertThresholdPercent: 80,
    active: true,
    createdAt: timestamp,
    updatedAt: timestamp,
  } as BudgetCode
  const ref = await addDoc(budgetCodesRef(), document)
  return { ...document, id: ref.id }
}

/** Field-level patch. `userId`, `fiscalYear`, and `external` are not editable. */
export type BudgetCodeUpdate = Partial<
  Pick<
    BudgetCode,
    | 'code'
    | 'displayName'
    | 'shortLabel'
    | 'fund'
    | 'category'
    | 'aliases'
    | 'beginningBalance'
    | 'alertThresholdPercent'
    | 'externalOwnerName'
    | 'active'
  >
>

export async function updateBudgetCode(
  id: string,
  patch: BudgetCodeUpdate,
): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.budgetCodes, id), {
    ...patch,
    updatedAt: new Date(),
  })
}

/**
 * Edit an external code from the values its form owns (workflow 7.7).
 *
 * `displayName` is written alongside `externalOwnerName` because on an external
 * code they are the same string — see `createExternalBudgetCode`. Lookup, the
 * picker and the alias uniqueness check all read `displayName`, so renaming the
 * owner without it leaves the old name showing everywhere the code is chosen.
 *
 * `shortLabel` falls back to the owner name the same way creation does: the
 * field is optional, and an emptied one has to mean "default it again", not
 * "store a blank the packet will print".
 *
 * Everything else the document carries — `fund`, `category`, the inert balance
 * fields, `active` — is left untouched rather than re-derived, so nothing this
 * form doesn't show can be blanked by saving it.
 */
export async function updateExternalBudgetCode(
  id: string,
  input: NewExternalBudgetCode,
): Promise<void> {
  await updateBudgetCode(id, {
    code: input.code,
    externalOwnerName: input.externalOwnerName,
    displayName: input.externalOwnerName,
    shortLabel: input.shortLabel?.trim() || input.externalOwnerName,
    aliases: input.aliases,
  })
}

/**
 * Soft delete (§5.2 `active`). A code with transactions behind it is never
 * hard-deleted: they reference it, and removing it would leave orphaned rows on
 * a reconciliation packet that has already been submitted.
 */
export async function setBudgetCodeActive(
  id: string,
  active: boolean,
): Promise<void> {
  await updateBudgetCode(id, { active })
}

/**
 * How many transactions point at a code.
 *
 * Two equality filters and no ordering, so no composite index is needed — see
 * the index table in CLAUDE.md. Deliberately not scoped to a fiscal year: this
 * decides whether a document can be destroyed, and a reference from any year
 * has to count.
 */
export async function countTransactionsForCode(
  userId: string,
  codeId: string,
): Promise<number> {
  const snapshot = await getCountFromServer(
    query(
      collection(db, COLLECTIONS.transactions),
      where('userId', '==', userId),
      where('budgetCodeId', '==', codeId),
    ),
  )
  return snapshot.data().count
}

/**
 * Remove a code: delete it outright when nothing references it, deactivate it
 * when something does. Returns which happened, so the screen can say so.
 *
 * The count is taken here rather than accepted from the caller. The button is
 * labelled from a cached count that may be a refetch behind, and the difference
 * between the two branches is a document that cannot be brought back.
 */
export async function removeBudgetCode(
  userId: string,
  id: string,
): Promise<'deleted' | 'archived'> {
  if ((await countTransactionsForCode(userId, id)) > 0) {
    await setBudgetCodeActive(id, false)
    return 'archived'
  }
  await deleteDoc(doc(db, COLLECTIONS.budgetCodes, id))
  return 'deleted'
}

/** Create many codes at once, for the first-run setup wizard (Step 4). */
export async function createBudgetCodesBatch(
  userId: string,
  fiscalYear: number,
  inputs: readonly NewOwnedBudgetCode[],
): Promise<void> {
  const batch = writeBatch(db)
  const timestamp = nowIso()
  for (const input of inputs) {
    batch.set(budgetCodeRef(doc(collection(db, COLLECTIONS.budgetCodes)).id), {
      ...input,
      userId,
      fiscalYear,
      external: false,
      externalOwnerName: null,
      active: true,
      createdAt: timestamp,
      updatedAt: timestamp,
    } as BudgetCode)
  }
  await batch.commit()
}

/** A starting balance to overwrite on a code that already exists. */
export interface BalanceUpdate {
  id: string
  beginningBalance: number
}

/**
 * Write a budget spreadsheet import: new codes, plus the balance updates the
 * person chose to apply to codes that already existed.
 *
 * Batched, and split below Firestore's 500-writes-per-batch limit. A building
 * budget is 70 lines today, but a district-wide file would pass 500, and a
 * batch over the limit fails whole — which is the right failure, but not one to
 * reach for a file that is merely large.
 *
 * An update touches `beginningBalance` and nothing else. The name, short label
 * and aliases on an existing code were set by a person, possibly on purpose,
 * and an import re-reading the budget has no business reverting them.
 */
export async function applyBudgetImport(
  userId: string,
  fiscalYear: number,
  creates: readonly NewOwnedBudgetCode[],
  updates: readonly BalanceUpdate[],
): Promise<void> {
  const timestamp = nowIso()
  const writes: ((batch: ReturnType<typeof writeBatch>) => void)[] = [
    ...creates.map((input) => (batch: ReturnType<typeof writeBatch>) =>
      batch.set(budgetCodeRef(doc(collection(db, COLLECTIONS.budgetCodes)).id), {
        ...input,
        userId,
        fiscalYear,
        external: false,
        externalOwnerName: null,
        active: true,
        createdAt: timestamp,
        updatedAt: timestamp,
      } as BudgetCode),
    ),
    ...updates.map((update) => (batch: ReturnType<typeof writeBatch>) =>
      batch.update(doc(db, COLLECTIONS.budgetCodes, update.id), {
        beginningBalance: update.beginningBalance,
        updatedAt: new Date(),
      }),
    ),
  ]

  const BATCH_LIMIT = 400
  for (let start = 0; start < writes.length; start += BATCH_LIMIT) {
    const batch = writeBatch(db)
    for (const write of writes.slice(start, start + BATCH_LIMIT)) write(batch)
    await batch.commit()
  }
}
